import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft, CalendarDays, MapPin, Clock, DollarSign, TrendingUp, Receipt, ClipboardList,
  Sparkles, Trash2, Pencil, Save, MessageSquare, Star, ThumbsUp, Meh, ThumbsDown, BarChart3,
  Package, Award, Percent,
} from "lucide-react";
import {
  getEvent, saveEventReport, formatCurrency, getEventSummary, deleteEvent, saveEvent,
} from "../lib/storage";
import { getEventProductSummary } from "../lib/products";
import { useProState } from "../lib/pro";
import ProBadge from "../components/ProBadge";
import Paywall from "../components/Paywall";
import { toast } from "sonner";

const verdictTone = {
  good: "bg-brand-success/10 text-brand-success border-brand-success/30",
  risky: "bg-brand-warning/15 text-brand-warning border-brand-warning/30",
  bad: "bg-brand-error/10 text-brand-error border-brand-error/30",
  neutral: "bg-brand-cream-soft text-brand-mute border-brand-line",
};

const emptyReport = {
  actualRevenue: "",
  actualSales: "",
  actualBoothCost: "",
  actualTravelCost: "",
  actualSuppliesCost: "",
  trafficNotes: "",
  bestSellerNotes: "",
  repeatChoice: "",
};

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { pro } = useProState();
  const [event, setEvent] = useState(null);
  const [report, setReport] = useState(emptyReport);

  useEffect(() => {
    const e = getEvent(id);
    if (!e) {
      toast.error("Event not found");
      navigate("/planner");
      return;
    }
    setEvent(e);
    setReport({ ...emptyReport, ...(e.report || {}) });
  }, [id, navigate]);

  const summary = useMemo(() => (event ? getEventSummary(event) : null), [event]);
  const productSummary = useMemo(() => (event ? getEventProductSummary(event) : null), [event]);

  if (!event || !summary) return null;

  const dateLabel = event.date
    ? new Date(event.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })
    : "Date TBD";

  const setR = (k) => (e) => setReport((s) => ({ ...s, [k]: e.target.value }));

  const onSaveReport = () => {
    const updated = saveEventReport(event.id, report);
    if (updated) {
      setEvent(updated);
      toast.success("Post-event report saved");
    }
  };

  const onDelete = () => {
    const name = event?.name?.trim() || "this event";
    toast(`Delete "${name}"?`, {
      description: "This can't be undone.",
      duration: 6000,
      action: {
        label: "Delete",
        onClick: () => {
          deleteEvent(event.id);
          toast.success("Event deleted");
          navigate("/planner");
        },
      },
    });
  };

  const onClearReport = () => {
    toast("Clear the post-event report?", {
      description: "Your actual numbers will be removed.",
      duration: 6000,
      action: {
        label: "Clear",
        onClick: () => {
          const next = { ...event, report: undefined };
          saveEvent(next);
          setEvent(next);
          setReport(emptyReport);
          toast.success("Report cleared");
        },
      },
    });
  };

  const repeatOptions = [
    { value: "yes", label: "Yes", icon: ThumbsUp, tone: "good" },
    { value: "maybe", label: "Maybe", icon: Meh, tone: "risky" },
    { value: "no", label: "No", icon: ThumbsDown, tone: "bad" },
  ];

  const profitDiff = summary.diff;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 pb-24 md:pb-16" data-testid="event-detail-page">
      {/* Top bar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <Link to="/planner" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-mute hover:text-brand-green" data-testid="event-back">
          <ArrowLeft className="w-4 h-4" /> All events
        </Link>
        <div className="flex items-center gap-2">
          <Link to={`/planner`} state={{ editId: event.id }} className="pop-btn-secondary" data-testid="event-edit-btn">
            <Pencil className="w-4 h-4" /> Edit
          </Link>
          <button onClick={onDelete} className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 font-medium transition-all text-brand-error hover:bg-brand-error/10 active:scale-[0.97]" data-testid="event-detail-delete">
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="pop-card p-6 sm:p-8 mb-6">
        <p className="label-eyebrow">Event</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-brand-ink mt-2">{event.name}</h1>
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-brand-mute">
          <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" strokeWidth={2} />{dateLabel}</span>
          {event.location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" strokeWidth={2} />{event.location}</span>}
          {(event.startTime || event.endTime) && (
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" strokeWidth={2} />{event.startTime || "?"} – {event.endTime || "?"}</span>
          )}
        </div>

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat label="Est. profit" value={formatCurrency(summary.estimate.profit)} testid="detail-est-profit" />
          <Stat label="Est. costs" value={formatCurrency(summary.estimate.costs)} testid="detail-est-costs" />
          <Stat label="Actual profit" value={summary.actual ? formatCurrency(summary.actual.profit) : "—"} testid="detail-actual-profit" highlight={!!summary.actual} />
          <Stat label="Difference" value={summary.actual ? `${profitDiff >= 0 ? "+" : ""}${formatCurrency(profitDiff)}` : "—"} testid="detail-diff" highlight={!!summary.actual && profitDiff >= 0} negative={summary.actual && profitDiff < 0} />
        </div>

        <div className="mt-5 flex items-center gap-2 flex-wrap">
          <span className={`pop-pill border ${verdictTone[summary.repeat.tone]}`} data-testid="detail-verdict">
            {summary.repeat.label}
          </span>
          {summary.actual && (
            <span className={`pop-pill border ${summary.goalHit ? "bg-brand-success/10 text-brand-success border-brand-success/30" : "bg-brand-warning/15 text-brand-warning border-brand-warning/30"}`} data-testid="detail-goal">
              <Star className="w-3 h-3" strokeWidth={2.5} /> {summary.goalHit ? "Sales goal hit" : "Sales goal missed"}
            </span>
          )}
        </div>
      </section>

      {/* Estimate breakdown */}
      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        <section className="pop-card p-6" data-testid="detail-estimate-card">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-9 h-9 rounded-xl bg-brand-green/10 text-brand-green grid place-items-center">
              <TrendingUp className="w-5 h-5" strokeWidth={2} />
            </span>
            <h2 className="font-heading text-lg font-semibold text-brand-ink">Estimate</h2>
          </div>
          <BreakdownRow label="Expected revenue" value={formatCurrency(summary.estimate.revenue)} />
          <BreakdownRow label="Total costs" value={formatCurrency(summary.estimate.costs)} />
          <BreakdownRow label="Break-even sales" value={`${summary.estimate.breakEvenSales}`} />
          <BreakdownRow label="Profit margin" value={`${summary.estimate.margin.toFixed(1)}%`} />
          <div className="mt-3 pt-3 border-t border-brand-line">
            <BreakdownRow label="Estimated profit" value={formatCurrency(summary.estimate.profit)} strong />
          </div>
        </section>

        <section className="pop-card p-6" data-testid="detail-actual-card">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-9 h-9 rounded-xl bg-brand-gold/15 text-brand-gold grid place-items-center">
              <Sparkles className="w-5 h-5" strokeWidth={2} />
            </span>
            <h2 className="font-heading text-lg font-semibold text-brand-ink">Actual</h2>
            {!summary.actual && <span className="pop-pill bg-brand-cream-soft text-brand-mute border border-brand-line">Pending</span>}
          </div>
          {summary.actual ? (
            <>
              <BreakdownRow label="Actual revenue" value={formatCurrency(summary.actual.revenue)} />
              <BreakdownRow label="Actual sales" value={`${summary.actual.sales}`} />
              <BreakdownRow label="Actual costs" value={formatCurrency(summary.actual.costs)} />
              <BreakdownRow label="Actual margin" value={`${summary.actual.margin.toFixed(1)}%`} />
              <div className="mt-3 pt-3 border-t border-brand-line">
                <BreakdownRow label="Actual profit" value={formatCurrency(summary.actual.profit)} strong />
              </div>
            </>
          ) : (
            <p className="text-sm text-brand-mute">Fill in the post-event report below to see how the event actually went.</p>
          )}
        </section>
      </div>

      {/* Post-event report form */}
      {pro ? (
      <>
      <section className="pop-card p-6 sm:p-8" data-testid="report-form">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
          <div className="flex items-center gap-2">
            <span className="w-10 h-10 rounded-xl bg-brand-green/10 text-brand-green grid place-items-center">
              <ClipboardList className="w-5 h-5" strokeWidth={2} />
            </span>
            <div>
              <p className="label-eyebrow">Post-event report</p>
              <h2 className="font-heading text-xl font-semibold text-brand-ink mt-1 flex items-center gap-2">
                How did it really go?
                <ProBadge />
              </h2>
            </div>
          </div>
          {event.report?.completed && (
            <button onClick={onClearReport} className="text-xs font-medium text-brand-mute hover:text-brand-error" data-testid="report-clear">
              Clear report
            </button>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <CurrencyField id="report-actualRevenue" label="Actual sales revenue" value={report.actualRevenue} onChange={setR("actualRevenue")} />
          <NumberField id="report-actualSales" label="Actual number of sales" value={report.actualSales} onChange={setR("actualSales")} />
          <CurrencyField id="report-actualBoothCost" label="Actual booth costs" value={report.actualBoothCost} onChange={setR("actualBoothCost")} />
          <CurrencyField id="report-actualTravelCost" label="Actual travel costs" value={report.actualTravelCost} onChange={setR("actualTravelCost")} />
          <CurrencyField id="report-actualSuppliesCost" label="Actual supplies costs" value={report.actualSuppliesCost} onChange={setR("actualSuppliesCost")} />
        </div>

        <div className="mt-5">
          <label className="pop-label flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5 text-brand-mute" /> Traffic notes</label>
          <textarea rows="2" className="pop-input resize-none" value={report.trafficNotes} onChange={setR("trafficNotes")} placeholder="Slow morning, busy from noon onwards…" data-testid="report-trafficNotes" />
        </div>
        <div className="mt-4">
          <label className="pop-label flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-brand-mute" /> Best-selling products</label>
          <textarea rows="2" className="pop-input resize-none" value={report.bestSellerNotes} onChange={setR("bestSellerNotes")} placeholder="Lavender soap sold out by 1 PM…" data-testid="report-bestSellerNotes" />
        </div>

        <div className="mt-6">
          <p className="pop-label">Would you do this event again?</p>
          <div className="grid grid-cols-3 gap-2.5">
            {repeatOptions.map(({ value, label, icon: Icon, tone }) => {
              const active = report.repeatChoice === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setReport((s) => ({ ...s, repeatChoice: value }))}
                  className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl border p-4 transition-all ${
                    active
                      ? `${verdictTone[tone]} ring-2 ring-offset-2 ring-offset-white`
                      : "bg-white border-brand-line text-brand-mute hover:border-brand-gold/40"
                  }`}
                  data-testid={`report-repeat-${value}`}
                  aria-pressed={active}
                >
                  <Icon className="w-5 h-5" strokeWidth={2} />
                  <span className="text-sm font-semibold">{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-7 flex flex-wrap gap-3 justify-end">
          <Link to="/compare" className="pop-btn-secondary" data-testid="report-compare-link">
            <BarChart3 className="w-4 h-4" /> Compare events
          </Link>
          <button onClick={onSaveReport} className="pop-btn-primary" data-testid="report-save">
            <Save className="w-4 h-4" /> Save report
          </button>
        </div>
      </section>

      {/* Products Performance — Pro feature, shown when event has products attached */}
      {productSummary && productSummary.count > 0 && (
        <section className="pop-card p-6 sm:p-8 mt-6" data-testid="products-performance-card">
          <div className="flex items-center gap-2.5 mb-1">
            <span className="w-10 h-10 rounded-xl bg-brand-green/10 text-brand-green grid place-items-center">
              <Package className="w-5 h-5" strokeWidth={2} />
            </span>
            <div>
              <p className="label-eyebrow">Products performance</p>
              <h2 className="font-heading text-xl font-semibold text-brand-ink mt-1 flex items-center gap-2">
                {productSummary.count} product{productSummary.count === 1 ? "" : "s"} tracked
                <ProBadge />
              </h2>
            </div>
          </div>

          {/* Totals */}
          <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <ProductStat icon={DollarSign} label="Product revenue" value={formatCurrency(productSummary.totalRevenue)} testid="prod-stat-revenue" highlight={productSummary.totalRevenue > 0} />
            <ProductStat icon={Receipt} label="Product cost" value={formatCurrency(productSummary.totalCost)} testid="prod-stat-cost" />
            <ProductStat icon={TrendingUp} label="Product profit" value={formatCurrency(productSummary.totalProfit)} testid="prod-stat-profit" highlight={productSummary.totalProfit > 0} negative={productSummary.totalProfit < 0} />
            <ProductStat icon={Package} label="Unsold inventory" value={`${productSummary.totalUnsold}`} testid="prod-stat-unsold" />
          </div>

          {/* Best seller + highest margin */}
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <HighlightRow
              icon={Award}
              label="Best-selling product"
              value={productSummary.bestSeller ? productSummary.bestSeller.product.name : "—"}
              sub={productSummary.bestSeller ? `${productSummary.bestSeller.qtySold} sold` : "No sales recorded yet"}
              testid="prod-best-seller"
            />
            <HighlightRow
              icon={TrendingUp}
              label="Highest-profit product"
              value={productSummary.highestProfit ? productSummary.highestProfit.product.name : "—"}
              sub={productSummary.highestProfit ? `${formatCurrency(productSummary.highestProfit.profit)} profit` : "Record some sales to see this"}
              testid="prod-highest-profit"
            />
          </div>

          {/* Per-product table */}
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm" data-testid="prod-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-brand-mute border-b border-brand-line">
                  <th className="py-2 pr-3 font-semibold">Product</th>
                  <th className="py-2 px-3 font-semibold text-right">Brought</th>
                  <th className="py-2 px-3 font-semibold text-right">Sold</th>
                  <th className="py-2 px-3 font-semibold text-right">Revenue</th>
                  <th className="py-2 pl-3 font-semibold text-right">Profit</th>
                </tr>
              </thead>
              <tbody>
                {productSummary.lines.map(({ eventProduct, product, line }, i) => (
                  <tr key={i} className="border-b border-brand-line/60 last:border-0">
                    <td className="py-2.5 pr-3">
                      <p className="font-medium text-brand-ink truncate">{product?.name || <span className="text-brand-error">Deleted product</span>}</p>
                      {product?.category && <p className="text-[11px] text-brand-mute">{product.category}</p>}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-brand-ink">{line.qtyBrought || 0}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-brand-ink">{line.qtySold || 0}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-brand-ink">{formatCurrency(line.revenue)}</td>
                    <td className={`py-2.5 pl-3 text-right tabular-nums font-semibold ${line.profit > 0 ? "text-brand-green" : line.profit < 0 ? "text-brand-error" : "text-brand-ink"}`}>{formatCurrency(line.profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-brand-mute mt-4">
            Edit quantities or add more products via <button onClick={() => navigate("/planner")} className="text-brand-green hover:underline">the planner</button> · Products are saved in your <button onClick={() => navigate("/products")} className="text-brand-green hover:underline">Product Library</button>.
          </p>
        </section>
      )}
      </>
      ) : (
        <Paywall
          title="Post-event report is a Pro feature"
          description="Track your actual revenue, costs, and best-sellers — then see which events are worth repeating."
          features={["Actual vs. estimated profit", "Sales-goal hit tracking", "Repeat-event recommendations", "Traffic & best-seller notes"]}
          testid="report-paywall"
        />
      )}
    </div>
  );
}

function ProductStat({ icon: Icon, label, value, testid, highlight = false, negative = false }) {
  return (
    <div className="rounded-2xl bg-white border border-brand-line p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-brand-mute">
        <Icon className="w-4 h-4" strokeWidth={2} />
        <p className="text-[10px] uppercase tracking-wider font-semibold">{label}</p>
      </div>
      <p className={`font-heading text-lg sm:text-xl font-semibold mt-1.5 tabular-nums tracking-tight ${
        negative ? "text-brand-error" : highlight ? "text-brand-green" : "text-brand-ink"
      }`}>{value}</p>
    </div>
  );
}

function HighlightRow({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-2xl bg-brand-cream-soft p-4 flex items-start gap-3" data-testid={testid}>
      <span className="w-9 h-9 rounded-xl bg-brand-green/10 text-brand-green grid place-items-center shrink-0">
        <Icon className="w-5 h-5" strokeWidth={2} />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-mute">{label}</p>
        <p className="font-heading text-base font-semibold text-brand-ink mt-0.5 truncate">{value}</p>
        <p className="text-xs text-brand-mute mt-0.5">{sub}</p>
      </div>
    </div>
  );
}

function Stat({ label, value, testid, highlight = false, negative = false }) {
  return (
    <div className="rounded-2xl bg-white border border-brand-line p-4" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-mute">{label}</p>
      <p className={`font-heading text-xl sm:text-2xl font-semibold mt-1 tabular-nums tracking-tight ${
        negative ? "text-brand-error" : highlight ? "text-brand-green" : "text-brand-ink"
      }`}>{value}</p>
    </div>
  );
}

function BreakdownRow({ label, value, strong = false }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className={`text-sm ${strong ? "text-brand-ink font-semibold" : "text-brand-mute"}`}>{label}</span>
      <span className={`tabular-nums ${strong ? "font-heading text-lg font-semibold text-brand-ink" : "text-sm font-medium text-brand-ink"}`}>
        {value}
      </span>
    </div>
  );
}

function CurrencyField({ id, label, value, onChange }) {
  return (
    <div>
      <label className="pop-label" htmlFor={id}>{label}</label>
      <div className="relative">
        <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-mute pointer-events-none" />
        <input
          id={id}
          data-testid={id}
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          placeholder="0.00"
          value={value}
          onChange={onChange}
          className="pop-input pl-11"
        />
      </div>
    </div>
  );
}

function NumberField({ id, label, value, onChange }) {
  return (
    <div>
      <label className="pop-label" htmlFor={id}>{label}</label>
      <input
        id={id}
        data-testid={id}
        type="number"
        inputMode="numeric"
        min="0"
        placeholder="0"
        value={value}
        onChange={onChange}
        className="pop-input"
      />
    </div>
  );
}
